import { InfoCircleOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import ConfigProvider from 'antd/lib/config-provider';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { isArray, isNil } from 'lodash';
import React, { SyntheticEvent, useMemo } from 'react';

const BACKGROUND_COLOR = '#f5f5f5';
const BORDER_COLOR = '#d9d9d9';

interface IBaseFilterAccordionProps {
    children: React.ReactNode;
    label?: string;
    value?: unknown;
    showClear?: boolean;
    onClear?: () => void;
    tooltipTitle?: string;
}

export const BaseFilterAccordion: React.FC<IBaseFilterAccordionProps> = ({
    children,
    label = '',
    value,
    showClear = true,
    onClear,
    tooltipTitle = '',
}) => {
    const hasValue = useMemo(() => (isArray(value) ? value.length > 0 : !isNil(value)), [value]);
    const showClearButton = useMemo(() => hasValue && showClear, [hasValue, showClear]);

    const handleClear = (e: SyntheticEvent) => {
        e.stopPropagation();

        onClear && onClear();
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        contentPadding: '8px', // Padding for the collapse content
                    },
                },
            }}
        >
            <Collapse
                ghost
                destroyOnHidden={false}
                expandIconPosition="end"
                style={{
                    width: '100%',
                    borderRadius: 0,
                    borderTop: `1px solid ${BORDER_COLOR}`,
                }}
                items={[
                    {
                        key: '1',
                        label: label ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingLeft: '8px' }}>
                                <Typography.Text style={{ marginTop: '2px', fontWeight: hasValue ? 'bold' : 'normal' }}>
                                    {label}
                                    <SelectTooltip title={tooltipTitle} />
                                </Typography.Text>
                                {showClearButton && (
                                    <Button size="small" type="link" variant="text" onClick={handleClear}>
                                        Clear
                                    </Button>
                                )}
                            </div>
                        ) : null,
                        children,
                        styles: {
                            header: {
                                backgroundColor: BACKGROUND_COLOR,
                                borderRadius: 0,
                                padding: '14px 12px',
                            },
                            body: {
                                backgroundColor: BACKGROUND_COLOR,
                                borderRadius: 0,
                                padding: '0 10px 8px 8px',
                            },
                        },
                    },
                ]}
            />
        </ConfigProvider>
    );
};

const SelectTooltip: React.FC<{ title: string }> = ({ title }) => {
    if (!title) return null;

    return (
        <Tooltip placement="right" title={title}>
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
        </Tooltip>
    );
};
